import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ResidentSettingsNavigationQueryParameters from 'shared/constants/resident-settings-navigation-query-parameters';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import ReadResidentsQuery from 'features/residents/models/read-residents-query';
import ReadResidentsResponse from 'features/residents/models/read-residents-response';
import Resident from 'features/residents/models/resident';
import { FilterValueType } from 'features/residents/models/resident-settings-filter-value';

type Props = {
  residentSettings: ReadResidentsResponse | undefined;
  residentSettingsQuery: ReadResidentsQuery;
  applyNavigationFilters: (parentOrganisationUnit: OrganisationTreeNode, searchTerm: string) => void;
  clearSelectedResidentSettings: () => void;
  closeDetails: () => void;
  openDetailsInViewingMode: (resident: Resident) => void;
};

type PendingResidentNavigation = {
  residentOrganisationUnitId: string;
  organisationUnitIds: string[];
  searchTerm: string;
};

const findOrganisationUnit = (
  organisationUnits: OrganisationTreeNode[],
  organisationUnitId: string,
): OrganisationTreeNode | undefined => {
  for (const organisationUnit of organisationUnits) {
    if (organisationUnit.id === organisationUnitId) {
      return organisationUnit;
    }

    const matchingOrganisationUnit = findOrganisationUnit(organisationUnit.childOrganisationUnits, organisationUnitId);
    if (matchingOrganisationUnit) {
      return matchingOrganisationUnit;
    }
  }

  return undefined;
};

const getOrganisationUnitIds = (organisationUnit: OrganisationTreeNode): string[] => [
  organisationUnit.id,
  ...organisationUnit.childOrganisationUnits.flatMap(getOrganisationUnitIds),
];

const useResidentSettingsNavigationState = ({
  residentSettings,
  residentSettingsQuery,
  applyNavigationFilters,
  clearSelectedResidentSettings,
  closeDetails,
  openDetailsInViewingMode,
}: Readonly<Props>): void => {
  const location = useLocation();
  const { organisationUnitTree } = useOrganisationUnitTreeContext();
  const appliedNavigationKey = useRef<string | undefined>(undefined);
  const [pendingResidentNavigation, setPendingResidentNavigation] = useState<PendingResidentNavigation | undefined>(undefined);

  useEffect(() => {
    const searchParameters = new URLSearchParams(location.search);
    const residentOrganisationUnitId = searchParameters.get(ResidentSettingsNavigationQueryParameters.ResidentOrganisationUnitId);
    const parentOrganisationUnitId = searchParameters.get(ResidentSettingsNavigationQueryParameters.ParentOrganisationUnitId);
    const searchTerm = searchParameters.get(ResidentSettingsNavigationQueryParameters.SearchTerm);
    const navigationKey = `${location.key}|${residentOrganisationUnitId}|${parentOrganisationUnitId}|${searchTerm}`;

    if (
      !residentOrganisationUnitId ||
      !parentOrganisationUnitId ||
      searchTerm === null ||
      !organisationUnitTree ||
      appliedNavigationKey.current === navigationKey
    ) {
      return;
    }

    const parentOrganisationUnit = findOrganisationUnit(organisationUnitTree, parentOrganisationUnitId);
    if (!parentOrganisationUnit) {
      return;
    }

    closeDetails();
    clearSelectedResidentSettings();
    applyNavigationFilters(parentOrganisationUnit, searchTerm);
    setPendingResidentNavigation({
      residentOrganisationUnitId,
      organisationUnitIds: getOrganisationUnitIds(parentOrganisationUnit),
      searchTerm,
    });
    appliedNavigationKey.current = navigationKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search, organisationUnitTree]);

  useEffect(() => {
    if (!residentSettings || !pendingResidentNavigation) {
      return;
    }

    const organisationUnitFilter = residentSettingsQuery.filters.find(
      (filter) => filter.filterValueType === FilterValueType.OrganisationUnit,
    );
    const queryMatchesNavigation =
      residentSettingsQuery.searchQuery === pendingResidentNavigation.searchTerm &&
      residentSettingsQuery.filters.length === 1 &&
      organisationUnitFilter?.values.length === pendingResidentNavigation.organisationUnitIds.length &&
      pendingResidentNavigation.organisationUnitIds.every((organisationUnitId) => organisationUnitFilter.values.includes(organisationUnitId));

    if (!queryMatchesNavigation) {
      return;
    }

    const residentToOpen = residentSettings.residents.find(
      (resident) => resident.organisationUnitId === pendingResidentNavigation.residentOrganisationUnitId,
    );

    if (residentToOpen) {
      openDetailsInViewingMode(residentToOpen);
      setPendingResidentNavigation(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentSettings, residentSettingsQuery, pendingResidentNavigation]);
};

export default useResidentSettingsNavigationState;
